import React, { useEffect, useState } from 'react';
import { Box } from '../../../CommonComponents/OPL/Box/Box';
import { Entity } from '../Entity/Entity';
import { EntityListLoading } from './EntityList.loading';
import { EntityListEmpty } from './EntityList.empty';
import { DeleteEntity } from '../DeleteEntity/DeleteEntity';
import { ShareRevokeThirdPartyEntityModal } from '../ShareRevokeThirdPartyEntityModal/ShareRevokeThirdPartyEntityModal';
import { RenameEntity } from '../RenameEntity/RenameEntity';
import { ErrorState } from '../../../CommonComponents/OPL/AsyncStates/ErrorState/ErrorState';
import { FullPageLoading } from '../../../CommonComponents/OPL/FullpageLoading/FullPageLoading';
import { useEntityList } from './useEntityList';
import { useUploadingFiles } from '../../state/selectors';
import { EntitiesDTO, EntityDTO, FOLDER_404_ERROR } from '../../DocumentVault.meta';
import { VideoModal } from '../VideoModal/VideoModal';
import { useNavigate } from '../../hooks/useNavigate';
import { useRouteParams } from '../../hooks/useRouteParams';
import { OrganizeModal } from '../OrganizeModal/OrganizeModal';
import { UseEntityListActions } from '../../hooks/useBaseEntityList';
import { toast } from '../../../CommonComponents/OPL/Toast/Toast';
import { MESSAGES } from '../../../CommonConstants';
import { useDownloadFolder } from '../../hooks/useDownloadFolder';

export const EntityList = () => {
  const {
    isFetching,
    isDownloadingFile,
    currentVideoEntity,
    isError,
    error,
    refetch,
    data,
    actions: { onEntityPress, setCurrentVideoEntity },
  } = useEntityList();

  const {
    isDownloadingFolder,
    actions: { onEntityDownload: downloadFolderEntity },
  } = useDownloadFolder();

  const onEntityDownload = async (entity: EntityDTO) => {
    try {
      await downloadFolderEntity(entity);
    } catch (err) {
      const message = err instanceof Error && err.message ? err.message : MESSAGES.GENERIC_ERROR;
      toast.error(message);
    }
  };

  if (isFetching) {
    return <EntityListLoading />;
  }

  // If the folder does not exist, we want to route the user to the home folder
  // See the useListEntitiesQuery onError() method
  if (isError && error?.message !== FOLDER_404_ERROR) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" p={96}>
        <ErrorState onAction={refetch} />
      </Box>
    );
  }

  if (!data) {
    return null;
  }

  return (
    <EntityListContent
      isDownloading={isDownloadingFile || isDownloadingFolder}
      currentVideoEntity={currentVideoEntity}
      onEntityPress={onEntityPress}
      onEntityDownload={onEntityDownload}
      setCurrentVideoEntity={setCurrentVideoEntity}
      data={data}
    />
  );
};

interface EntityListContentProps extends UseEntityListActions {
  isDownloading: boolean;
  currentVideoEntity: EntityDTO | null;
  data: EntitiesDTO;
  onEntityDownload(data: EntityDTO): void;
}

const EntityListContent = ({
  isDownloading,
  currentVideoEntity,
  onEntityPress,
  setCurrentVideoEntity,
  onEntityDownload,
  data,
}: EntityListContentProps) => {
  const [entityToDelete, setEntityToDelete] = useState<EntityDTO | null>(null);
  const [entityToRename, setEntityToRename] = useState<EntityDTO | null>(null);
  const [showOrganizeModal, setShowOrganizeModal] = useState<EntityDTO | null>(null);
  const [entityForThirdPartyShare, setEntityForThirdPartyShare] = useState<EntityDTO | null>(null);

  const uploadingFiles = useUploadingFiles();

  const navigate = useNavigate();

  const { filterableByYear: routeFilterableByYear } = useRouteParams();

  const { filterableByYear } = data;

  useEffect(() => {
    if (filterableByYear !== routeFilterableByYear) {
      navigate({
        filterableByYear,
      });
    }
  }, [routeFilterableByYear, filterableByYear, navigate]);

  const { items } = data;

  if (items.length === 0 && Object.keys(uploadingFiles).length === 0) {
    return <EntityListEmpty />;
  }

  return (
    <>
      {isDownloading && <FullPageLoading />}

      <>
        {items.map((entity, index) => {
          return (
            <Entity
              key={index} // eslint-disable-line react/no-array-index-key
              entity={entity}
              onEntityClick={() => onEntityPress(entity)}
              onEntityDelete={() => setEntityToDelete(entity)}
              onRenameClick={() => setEntityToRename(entity)}
              borderBottom={index === items.length - 1 ? 'none' : '1px solid'}
              onOrganizeClick={() => setShowOrganizeModal(entity)}
              onEntityDownload={() => onEntityDownload(entity)}
              onThirdPartyShareClick={() => setEntityForThirdPartyShare(entity)}
            />
          );
        })}
      </>

      {entityToDelete && (
        <DeleteEntity entity={entityToDelete} onRequestClose={() => setEntityToDelete(null)} />
      )}

      {entityForThirdPartyShare && (
        <ShareRevokeThirdPartyEntityModal
          entity={entityForThirdPartyShare}
          onRequestClose={() => setEntityForThirdPartyShare(null)}
        />
      )}

      {entityToRename && (
        <RenameEntity entity={entityToRename} onClose={() => setEntityToRename(null)} />
      )}

      {showOrganizeModal && (
        <OrganizeModal
          selectedEntity={showOrganizeModal}
          onClose={() => setShowOrganizeModal(null)}
        />
      )}

      <VideoModal entity={currentVideoEntity} onClose={() => setCurrentVideoEntity(null)} />
    </>
  );
};
